import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  TaskInstance,
  ReassignInstanceRequest,
  useReassignInstance,
} from "@/lib/api/checklist";
import { toast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { fetchApiGet } from "@/lib/api/utils";
import { DbUser } from "@/lib/models";
import { UserCheck } from "lucide-react";
import { useListMinimalUsers } from "@/lib/api/users";

interface ReassignInstanceModalProps {
  open: boolean;
  onClose: () => void;
  instance: TaskInstance;
}

interface UsersResponse {
  data: DbUser[];
}

export function ReassignInstanceModal({
  open,
  onClose,
  instance,
}: ReassignInstanceModalProps) {
  const reassignInstance = useReassignInstance(instance.id);
  const minimalUserListResult = useListMinimalUsers();
  const users = (minimalUserListResult?.data?.data || []);

  const form = useForm<ReassignInstanceRequest>({
    resolver: zodResolver(ReassignInstanceRequest),
    defaultValues: {
      assigned_to_id: instance.assigned_to_id,
    },
  });

  const userOptions = users.map((user: DbUser) => ({
    value: user.id,
    label: user.name,
  }));

  const onSubmit = async (data: ReassignInstanceRequest) => {
    if (data.assigned_to_id === instance.assigned_to_id) {
      toast({
        title: "No change",
        description: "Please select a different assignee.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await reassignInstance.mutateAsync(data);
      if (!result?.success) {
        throw new Error(result?.message || "Failed to reassign task");
      }
      toast({
        title: "Task reassigned",
        description: "The task instance has been reassigned successfully.",
      });
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to reassign task instance.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl">Reassign Task</DialogTitle>
          <DialogDescription className="text-base">
            Reassign "{instance.task?.title}" to a different user
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
            <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">Current Assignee</span>
                <span className="text-sm font-semibold">
                  {instance.assigned_to
                    ? instance.assigned_to.name
                    : "Unassigned"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">Due Date</span>
                <span className="text-sm font-semibold">
                  {new Date(instance.due_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
              {instance.is_late && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-muted-foreground">Status</span>
                  <span className="text-sm font-semibold text-destructive">Overdue</span>
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="assigned_to_id"
              render={({ field }) => (
                <FormItem className="space-y-3 grid">
                  <FormLabel className="text-sm font-medium">New Assignee</FormLabel>
                  <FormControl>
                    <Combobox
                      value={field.value}
                      onValueChange={field.onChange}
                      options={userOptions}
                      placeholder="Select new assignee"
                      dwimOnChange
                      isModal
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Select the user who will be responsible for this task
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={reassignInstance.isLoading}>
                <UserCheck className="h-4 w-4 mr-2" />
                {reassignInstance.isLoading ? "Reassigning..." : "Reassign Task"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
